
import Vue from 'vue';
import RunnitDynamicFieldLabelRow from '@/views/Runnits/RunnitSettings/RunnitDynamicFieldLabelRow.vue';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import BaseSlider from '@/components/base/BaseSlider.vue';
import { RunnitNodeField } from '@run-diffusion/shared';
import _mapKeys from 'lodash/mapKeys';
import AspectRatioSVG from '@/assets/AspectRatioSVG.vue';
import { getLogicOnFieldsFromField } from '@run-diffusion/shared';
import _isNil from 'lodash/isNil';
import { mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { applyAspectRatioDisabledMessage } from '@/utils/aspectRatio';
import { dataSourceDimensions } from '@/views/Runnits/RunnitSettings/WidthHeightField/utils';

export default Vue.extend({
	name: 'WidthHeightField',
	mixins: [
		RunnitDynamicFieldMixin,
	],
	props: {
		field: { type: Object, required: true },
		value: { type: [String, Object], default: null },
		adminOnly: { type: Boolean, default: false },
		inputValues: { type: Object, required: true },
	},
	data () {
		return {
			MIN_DIMENSION: 8,
			DIVISIBLE_BY: 8,
			optionsMap: {},
		};
	},
	computed: {
		logicOnFields () {
			const logic = getLogicOnFieldsFromField(this.field, this.inputValues);
			return applyAspectRatioDisabledMessage(logic, this.field, this.inputValues);
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
		isSingularValueFormat () {
			return this._get(this.field, 'display.select.format') === 'value';
		},
		computedSelectFieldValue () {
			if (this.isSingularValueFormat) {
				return this.value || null;
			}
			if (!this.value || !this.value.width || !this.value.height) {
				return null;
			}
			// For 'wh' format, we need to return the full option object since v-select has return-object="true"
			const options = this._get(this.field, 'display.select.options') || [];
			const valueString = `${this.value.width}x${this.value.height}`;
			// Try to find by value string first, then by width/height match
			return options.find(option =>
				option.value === valueString ||
				(option.width === this.value.width && option.height === this.value.height)
			) || null;
		},
		sliderMinLimits () {
			const width: number = this._get(this.field, 'display.sliders.min[0]') || this.MIN_DIMENSION;
			const height: number = this._get(this.field, 'display.sliders.min[1]') || this.MIN_DIMENSION;
			return { width, height };
		},
		sliderMaxLimits () {
			const width: number = this._get(this.field, 'display.sliders.max[0]') || this.MIN_DIMENSION;
			const height: number = this._get(this.field, 'display.sliders.max[1]') || this.MIN_DIMENSION;
			return { width, height };
		},
		computedWidthAndHeight () {
			if (this.isSingularValueFormat) {
				const { width, height } = (this.value && this.optionsMap[this.value]) || {
					width: this.sliderMinLimits.width,
					height: this.sliderMinLimits.height,
				};
				return { width, height };
			}
			if (this.value && (this.value.width || this.value.height)) {
				const width: number = this.value.width || this.sliderMinLimits.width;
				const height: number = this.value.height || this.sliderMinLimits.height;
				return { width, height };
			}
			return {
				width: this.sliderMinLimits.width,
				height: this.sliderMinLimits.height,
			};
		},
		dataInputValue () {
			// Check for new unified field first, then fall back to legacy image field
			const dataFieldDefUuid = this._get(this.field, 'display.dataFieldDefUuid');
			if (dataFieldDefUuid) {
				return this.inputValues[dataFieldDefUuid];
			}

			// Legacy support for imageFieldDefUuid only (videoInputValue is new and can use dataFieldDefUuid)
			const imageFieldDefUuid = this._get(this.field, 'display.imageFieldDefUuid');
			if (imageFieldDefUuid) {
				return this.inputValues[imageFieldDefUuid];
			}

			return null;
		},
		dataInputDimensions () {
			return dataSourceDimensions(this.dataInputValue);
		},
		hasInputValue () {
			return !!this.dataInputDimensions;
		},
	},
	watch: {
		field: {
			immediate: true,
			handler (newVal: RunnitNodeField, oldVal: RunnitNodeField) {
				if (newVal !== oldVal) {
					this.optionsMap = _mapKeys(this._get(this.field, 'display.select.options') || [], 'value');
				}
			},
		},
		autoSetValue: {
			handler (newValue: any, oldValue: any) {
				if (_isNil(newValue) || newValue === oldValue) return;
				// Apply autoSet if it matches an available option
				const options = this._get(this.field, 'display.select.options') || [];
				if (this.isSingularValueFormat) {
					// Expect autoSetValue to be a value string that matches options.value
					const match = options.find((opt: any) => opt && opt.value === newValue);
					if (match) {
						this.$emit('input', match.value);
						return;
					}
				} else {
					// Expect autoSetValue to be an object with width/height matching an option
					const w = this._get(newValue, 'width');
					const h = this._get(newValue, 'height');
					if (w && h) {
						const match = options.find((opt: any) => opt && opt.width === w && opt.height === h);
						if (match) {
							this.emitWidthHeightSeperatelyInput(match.width, match.height);
							return;
						}
					}
				}
				// If no option matched, notify user with message if provided
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.autoSetError || 'Auto-set width/height did not match an available option.',
					show: true,
				});
			},
			immediate: true,
		},
		autoSetError: {
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newVal,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onSelectFieldInput ({ width, height, value }) {
			if (this.isSingularValueFormat) {
				this.$emit('input', value);
			} else {
				this.emitWidthHeightSeperatelyInput(width, height);
			}
		},
		onSliderWidthChange (width: number) {
			this.emitWidthHeightSeperatelyInput(width, this.computedWidthAndHeight.height);
		},
		onSliderHeightChange (height: number) {
			this.emitWidthHeightSeperatelyInput(this.computedWidthAndHeight.width, height);
		},
		emitWidthHeightSeperatelyInput (width: number, height: number) {
			this.$emit(
				'input',
				{
					width: width > 0 ? width : this.sliderMinLimits.width,
					height: height > 0 ? height : this.sliderMinLimits.height,
				},
			);
		},
		setFromData () {
			if (!this.dataInputDimensions) return;
			const { width, height } = this.dataInputDimensions;

			// Calculate the aspect ratio of the input
			const inputAspectRatio = width / height;

			// First pass: find the smallest aspect ratio difference
			let smallestAspectRatioDiff = Infinity;
			this._get(this.field, 'display.select.options').forEach((option) => {
				const optionAspectRatio = option.width / option.height;
				const diff = Math.abs(optionAspectRatio - inputAspectRatio);
				if (diff < smallestAspectRatioDiff) {
					smallestAspectRatioDiff = diff;
				}
			});

			// Second pass: among options with the closest aspect ratio, find the one with closest dimensions
			let closestOption = this._get(this.field, 'display.select.options')[0];
			let smallestDimensionDiff = Infinity;
			this._get(this.field, 'display.select.options').forEach((option) => {
				const optionAspectRatio = option.width / option.height;
				const aspectRatioDiff = Math.abs(optionAspectRatio - inputAspectRatio);

				// Only consider options with the closest aspect ratio
				if (Math.abs(aspectRatioDiff - smallestAspectRatioDiff) < 0.0001) {
					const diffWidth = Math.abs(option.width - width);
					const diffHeight = Math.abs(option.height - height);
					const dimensionDiff = diffWidth + diffHeight;

					if (dimensionDiff < smallestDimensionDiff) {
						closestOption = option;
						smallestDimensionDiff = dimensionDiff;
					}
				}
			});

			if (this.isSingularValueFormat) {
				this.$emit('input', closestOption.value);
			} else {
				this.emitWidthHeightSeperatelyInput(closestOption.width, closestOption.height);
			}
			// The prepend-item row is not one of the select's items, so the menu does not close on its own
			(this.$refs.sizeSelect as Vue & { blur: () => void }).blur();
		},
		isUltra (width: number, height: number): boolean {
			return width * height >= 1200000
		},
	},
	components: {
		BaseSlider,
		RunnitDynamicFieldLabelRow,
		AspectRatioSVG,
	},
});
